import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { X, Plus, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { toast } from "./Toast"
import { darfAufgabeAnlegen } from "@/lib/aufgabeVerknuepfung"
import { wegSchritt } from "@/lib/wege"
import { herkunftHinweis, useAufgabeVerknuepfung, type VerknuepfungVorgang } from "@/hooks/useAufgabeVerknuepfung"
import { CreateContactModal } from "./CreateContactModal"

type Assignee = { user_id: number; name: string }
type HeroContact = { id: number; first_name: string; last_name: string; phone?: string; email?: string }
type HeroProject = { id: number; name: string; status?: string }

/**
 * Aufgabe aus Call-Kontext (Sipgate/Meiti) anlegen — mit Kontakt-Suche und
 * Projekt-Auswahl. 1:1-Port von sg-task-modal / call-task-modal aus altem Portal.
 */
export function CallTaskModal({
  open, onClose, mode, callId, tenantSlug, defaultTitle, defaultComment,
  defaultContactId, defaultContactName, defaultDueDate, defaultProjectId, defaultAssignee, onZwischenantwort,
}: {
  open: boolean
  onClose: () => void
  mode: "sipgate" | "meiti" | "email" | "voice" | "whatsapp"
  callId: number | string
  tenantSlug: string
  defaultTitle?: string
  defaultComment?: string
  defaultContactId?: number | null
  defaultContactName?: string
  /** WhatsApp-Vorschlag: Faelligkeit und Projekt aus dem Vorschlag. */
  defaultDueDate?: string | null
  defaultProjectId?: number | null
  /** Mitarbeiter im Format „user_id|name“ (Plan-Aufgabe, #128). */
  defaultAssignee?: string | null
  /** #128: Der Plan hat eine Zwischenantwort an den Kunden vorbereitet — nach dem Anlegen (oder per Knopf)
   *  öffnet sie sich im Antwort-Formular. Gesendet wird dort wie immer erst mit „Senden“. */
  onZwischenantwort?: () => void
}) {
  const qc = useQueryClient()
  const [title, setTitle] = useState("")
  const [comment, setComment] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [assignee, setAssignee] = useState("")
  const [contactSearch, setContactSearch] = useState("")
  const [contactId, setContactId] = useState<number | null>(null)
  const [contactName, setContactName] = useState("")
  const [searchResults, setSearchResults] = useState<HeroContact[]>([])
  const [projectId, setProjectId] = useState("")
  const [projects, setProjects] = useState<HeroProject[]>([])
  const [err, setErr] = useState<string | null>(null)
  // „Wenn ich eine Aufgabe anlegen möchte und den Kontakt vorher noch nicht
  // angelegt habe, sollte ich das hier nachholen können." (Marlene, 02.09.2026)
  //
  // 🔑 Es haengt mehr daran als die Bequemlichkeit: die Projekt-Auswahl unten
  // erscheint erst MIT Kontakt (ein Vorgang haengt in HERO am Kunden). Ohne
  // einen Weg, den Kontakt hier anzulegen, blieb eine Aufgabe ohne Kontakt
  // UND ohne Vorgang — und das Getippte war beim Umweg ueber eine andere
  // Seite verloren.
  const [kontaktAnlegen, setKontaktAnlegen] = useState(false)
  // #108 — „Projekte sollten auch bei ‚manueller' Aufgabenanlage automatisch
  // verknüpft sein" (Paul, 17.09.2026). Aus dem Posteingang kam dieser Dialog
  // ganz ohne Vorbelegung, selbst wenn die Mail laengst verknuepft war. Die
  // Regel liegt auf dem Server; hier nur fuer Mails — Anrufe bringen ihren
  // Kontakt ueber `defaultContactId` mit.
  const vorschlag = useAufgabeVerknuepfung(mode === "email" ? callId : null, open && mode === "email")
  const [kontaktHinweis, setKontaktHinweis] = useState("")
  const [vorgangVorschlag, setVorgangVorschlag] = useState<VerknuepfungVorgang | null>(null)
  // Wer selbst gewaehlt oder entfernt hat, wird von einem spaet eintreffenden
  // Vorschlag nicht mehr ueberschrieben.
  const angefasst = useRef(false)

  const loadProjectsFor = async (cid: number, name: string) => {
    try {
      const lastName = name.split(" ").pop() || name
      const res = await fetch(
        `/admin/tasks/api/search-projects?tenant=${encodeURIComponent(tenantSlug)}&contact_id=${cid}&search=${encodeURIComponent(lastName)}`,
        { credentials: "include" },
      )
      const d = await res.json()
      setProjects(d.projects || [])
    } catch {
      setProjects([])
    }
  }

  useEffect(() => {
    if (open) {
      setTitle(defaultTitle || "")
      setComment(defaultComment || ""); setDueDate(defaultDueDate || ""); setAssignee(defaultAssignee || "")
      setContactSearch(""); setSearchResults([])
      setProjectId(defaultProjectId ? String(defaultProjectId) : ""); setProjects([])
      setErr(null)
      setKontaktHinweis(""); setVorgangVorschlag(null); angefasst.current = false
      // Aus dem Anruf bereits aufgeloesten Kontakt vorbefuellen (KI-Telefon).
      if (defaultContactId) {
        setContactId(defaultContactId)
        setContactName(defaultContactName || "")
        loadProjectsFor(defaultContactId, defaultContactName || "")
      } else {
        setContactId(null); setContactName("")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTitle, defaultComment, defaultContactId, defaultContactName, defaultDueDate, defaultProjectId,
      defaultAssignee])

  useEffect(() => {
    if (!open || !vorschlag.data || angefasst.current) return
    const { contact, project } = vorschlag.data
    if (contact) {
      setContactId(contact.id); setContactName(contact.name)
      setKontaktHinweis(herkunftHinweis(contact))
      loadProjectsFor(contact.id, contact.name)
    }
    if (project) {
      setProjectId(String(project.id)); setVorgangVorschlag(project)
    }
    // Dieselben Ausloeser wie der Effekt oben: der leert den Kontakt, sobald
    // sich ein Default aendert (der Aufgabentext laedt nach) — ohne sie waere
    // die Vorbelegung danach still weg.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vorschlag.data, defaultTitle, defaultComment, defaultContactId, defaultContactName])

  const assignees = useQuery<{ monteure: Assignee[]; hinweis?: string | null }>({
    queryKey: ["task-assignees", tenantSlug],
    queryFn: async () => {
      const res = await fetch(`/api/v1/tasks/assignees?tenant=${encodeURIComponent(tenantSlug)}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open && !!tenantSlug,
  })

  const searchContact = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `/admin/tasks/api/search-contact?tenant=${encodeURIComponent(tenantSlug)}&search=${encodeURIComponent(contactSearch)}`,
        { credentials: "include" },
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json() as Promise<{ results: HeroContact[] }>
    },
    onSuccess: (d) => setSearchResults(d.results || []),
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  const selectContact = async (c: HeroContact) => {
    const name = `${c.first_name} ${c.last_name}`.trim()
    angefasst.current = true
    setKontaktHinweis(""); setVorgangVorschlag(null); setProjectId("")
    setContactId(c.id)
    setContactName(name)
    setSearchResults([])
    await loadProjectsFor(c.id, name)
  }

  const clearContact = () => {
    angefasst.current = true
    setKontaktHinweis(""); setVorgangVorschlag(null)
    setContactId(null); setContactName(""); setProjectId(""); setProjects([])
  }

  const create = useMutation({
    mutationFn: async () => {
      // #44: aus diesem Dialog kamen #22, #39 und #58 — er gehoert gezaehlt.
      wegSchritt("aufgabe_aus_anruf")
      const url =
        mode === "sipgate" ? `/api/v1/sipgate/${callId}/create-task` :
        mode === "email"   ? `/api/v1/emails/${callId}/create-task` :
        mode === "voice"   ? `/admin/ki-telefon/api/call/${callId}/create-task` :
        mode === "whatsapp" ? `/api/v1/whatsapp/vorschlaege/${callId}/anlegen` :
        /* meiti */          `/api/v1/calls/${callId}/create-task`

      // HERO erlaubt nur entweder contact_id ODER project_match_id —
      // wenn beide ausgewaehlt: Projekt hat Vorrang (spezifischer).
      const useContact = !projectId && contactId
      const useProject = !!projectId

      // assignee ist im Format "user_id|name" (siehe <option>-Werte unten)
      const [assigneeUserIdStr, assigneeNameStr] = (assignee || "").split("|")
      const assigneeUserId = assigneeUserIdStr ? Number(assigneeUserIdStr) : null

      // Schema je Endpoint:
      //  - sipgate + meiti: altes admin-Schema (comment, target_user, Strings) —
      //    Backend parst "id|name" selbst und akzeptiert leere Strings
      //  - email (v1): Pydantic-Schema, target_user_id (int) + contact_id (int|null)
      const payload = mode === "email" || mode === "whatsapp"
        ? {
            title,
            body: comment,
            due_date: dueDate || null,
            target_user_id: assigneeUserId,
            target_user_name: assigneeNameStr || null,
            contact_id: useContact ? Number(contactId) : null,
            project_match_id: useProject ? Number(projectId) : null,
          }
        : {
            title, comment, due_date: dueDate, target_user: assignee,
            tenant_slug: tenantSlug,
            contact_id: useContact ? String(contactId) : "",
            project_match_id: useProject ? String(projectId) : "",
          }

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.error) throw new Error(json.error || (typeof json.detail === "string" ? json.detail : "") || `HTTP ${res.status}`)
      return json
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
      const detailKey = mode === "sipgate" ? "sipgate-call" : mode === "email" ? "email" : mode === "voice" ? "ki-telefon-call"
        : mode === "whatsapp" ? "whatsapp-verlauf" : "meiti-call"
      qc.invalidateQueries({ queryKey: [detailKey] })
      toast("✓ Aufgabe erstellt")
      onClose()
      onZwischenantwort?.()
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Aufgabe-Erstellung fehlgeschlagen: " + msg, "error")
    },
  })

  if (!open) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      data-kontext-popup="Aufgabe anlegen"
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <form
        onSubmit={async (ev) => {
          ev.preventDefault(); setErr(null)
          // #149 (+ #22): Hinweis ohne Mitarbeiter / ohne Projekt — vor dem
          // Absenden, damit „Abbrechen" den Dialog offen laesst.
          if (!(await darfAufgabeAnlegen({ mitarbeiter: assignee, contactId, projectId }))) return
          create.mutate()
        }}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Aufgabe erstellen</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div style={{ padding: "4px 20px 0 20px", fontSize: 12, color: "var(--text-muted)" }}>
          Tenant: <strong>{tenantSlug}</strong>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          {err && <div className="pp-alert pp-alert-err">{err}</div>}

          {onZwischenantwort && (
            <div className="pp-alert" data-testid="zwischenantwort-hinweis"
              style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
              <span>Der Plan hat eine Zwischenantwort an den Kunden vorbereitet. Sie öffnet sich nach dem
                Anlegen — gesendet wird erst mit „Senden“.</span>
              <button type="button" className="pp-btn pp-btn-sm"
                onClick={() => { onClose(); onZwischenantwort() }}>Nur Zwischenantwort öffnen</button>
            </div>
          )}

          <div>
            <label className="pp-form-label">Titel *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>

          <div>
            <label className="pp-form-label">Beschreibung</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="pp-form-label">Fälligkeitsdatum</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div>
              <label className="pp-form-label">Mitarbeiter</label>
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">-- kein Mitarbeiter --</option>
                {assignees.data?.monteure?.map((m) => (
                  <option key={m.user_id} value={`${m.user_id}|${m.name}`}>{m.name}</option>
                ))}
              </select>
              {assignees.data && !assignees.data.monteure?.length && (
                <div className="text-xs" data-testid="keine-mitarbeiter" style={{ marginTop: 4, color: "var(--text-muted, #6b7280)" }}>
                  {assignees.data.hinweis || "Keine Mitarbeiter hinterlegt."}
                </div>
              )}
              {assignees.isError && (
                <div className="text-xs" style={{ marginTop: 4, color: "#b91c1c" }}>Mitarbeiterliste konnte nicht geladen werden.</div>
              )}
            </div>
          </div>

          <div>
            <label className="pp-form-label">Kontakt suchen (Name, Telefon oder E-Mail)</label>
            {!contactId ? (
              <>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder="z.B. Mustermann"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => { setErr(null); searchContact.mutate() }}
                    disabled={!contactSearch.trim() || searchContact.isPending}
                    className="pp-btn pp-btn-sm"
                  >
                    <Search className="w-3 h-3" /> Suchen
                  </button>
                </div>
                {searchResults.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                    {searchResults.map((c) => {
                      const name = `${c.first_name} ${c.last_name}`.trim()
                      const info = [c.phone, c.email].filter(Boolean).join(" · ")
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => selectContact(c)}
                          style={{
                            padding: "6px 10px", background: "#f0fdf4",
                            border: "1px solid #bbf7d0", borderRadius: 6,
                            fontSize: 12, cursor: "pointer", textAlign: "left",
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                          }}
                        >
                          <span><strong>{name}</strong>{info && <span style={{ color: "#6b7280", marginLeft: 6 }}>{info}</span>}</span>
                          <span style={{ color: "var(--accent-color)", fontSize: 12 }}>Wählen</span>
                        </button>
                      )
                    })}
                  </div>
                )}
                {searchContact.isSuccess && searchResults.length === 0 && (
                  <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Kein Kontakt gefunden</span>
                    <button
                      type="button"
                      className="pp-btn pp-btn-sm"
                      onClick={() => setKontaktAnlegen(true)}
                    >
                      <Plus className="w-3 h-3" /> Kontakt anlegen
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: "6px 10px", background: "#d1fae5", border: "1px solid #10b981",
                           borderRadius: 6, fontSize: 12, color: "#065f46",
                           display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                  <strong>{contactName || "Kontakt"}</strong>
                  {kontaktHinweis && <span data-testid="kontakt-herkunft" style={{ display: "block", fontSize: 11 }}>{kontaktHinweis}</span>}
                </span>
                <button type="button" onClick={clearContact} style={{
                  background: "none", border: "none", color: "#ef4444",
                  cursor: "pointer", fontSize: 14,
                }}>✕</button>
              </div>
            )}
          </div>

          {/* „Hier wäre es gut, wenn man auch ein Projekt verknüpfen könnte"
              (Marlene, 02.09.2026). Das Feld GAB es — es erscheint nur mit
              Kontakt, weil ein Vorgang in HERO am Kunden haengt. Wortlos
              wegzulassen sah aus wie „gibt es nicht"; jetzt steht der Grund da. */}
          {!contactId && !projectId && (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              <span className="pp-form-label" style={{ display: "block" }}>Projekt</span>
              Erst den Kontakt wählen — die Vorgänge hängen in HERO am Kunden.
            </div>
          )}

          {(contactId || projectId) && (
            <div>
              <label className="pp-form-label">Projekt</label>
              <select value={projectId} onChange={(e) => { angefasst.current = true; setProjectId(e.target.value) }}>
                <option value="">-- kein Projekt --</option>
                {/* Der vorbelegte Vorgang braucht einen EIGENEN Eintrag: die
                    Liste kommt spaeter und aus einer HERO-Suche, die ihn nicht
                    enthalten muss — ohne ihn zeigt das Feld „kein Projekt",
                    waehrend im Zustand die ID steht. */}
                {vorgangVorschlag && !projects.some((p) => String(p.id) === String(vorgangVorschlag.id)) && (
                  <option value={String(vorgangVorschlag.id)}>
                    {vorgangVorschlag.bezeichnung}{vorgangVorschlag.status ? ` (${vorgangVorschlag.status})` : ""}
                  </option>
                )}
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.status ? ` (${p.status})` : ""}</option>
                ))}
              </select>
              {vorgangVorschlag && String(vorgangVorschlag.id) === String(projectId) && (
                <div data-testid="projekt-herkunft" style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                  {herkunftHinweis(vorgangVorschlag)}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="pp-btn pp-btn-sm">Abbrechen</button>
          <button
            type="submit"
            disabled={!title || create.isPending}
            className={cn("pp-btn pp-btn-primary pp-btn-sm", (!title || create.isPending) && "opacity-50 cursor-not-allowed")}
          >
            {create.isPending ? "Erstellt..." : "Aufgabe erstellen"}
          </button>
        </div>
      </form>

      {/* Liegt ueber dem Formular, nicht daneben: das Getippte bleibt stehen,
          und der frisch angelegte Kontakt ist danach direkt gewaehlt — samt
          seiner Vorgaenge, die dann in der Projekt-Auswahl auftauchen. */}
      <CreateContactModal
        open={kontaktAnlegen}
        onClose={() => setKontaktAnlegen(false)}
        mode="portal"
        itemId={0}
        tenantSlug={tenantSlug}
        prefillLast={contactSearch.trim()}
        onSuccess={async (ergebnis) => {
          setKontaktAnlegen(false)
          const neueId = ergebnis?.contact_id
          if (!neueId) return
          const name = contactSearch.trim() || `Kontakt #${neueId}`
          setContactId(neueId)
          setContactName(name)
          setSearchResults([])
          await loadProjectsFor(neueId, name)
        }}
      />
    </div>
  )
}
